import React, { Fragment, useMemo, useState } from 'react'
import injectSheet from 'react-jss'
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter
} from 'pobble-components/dist/modal'
import Button from 'pobble-components/dist/button'
import AccessibilitySelect from '../../../../../presentation-wizard/components/accessibility-select/accessibility-select'
import { Formik } from 'formik'
import FormSelect from '../../../../../common/form-elements/form-select/form-select'
import Label from '../../../../../presentation-wizard/components/label/label'
import YearGroupSelection from '../../../../../year-groups-selection/year-groups-selection'
import FormRow from 'pobble-components/dist/form-elements/form-row'
import FormCheckbox from 'pobble-components/dist/form-elements/form-checkbox'
import { useSelector } from 'react-redux'
import { isSettingsModalOpened } from '../../redux/builder.selectors'
import {
  getPresentation,
  getUpdatePropertiesRequest
} from '../../../../../../stores/presentation/presentation.selectors'
import { useDispatch } from 'react-redux'
import { closeSettingsModal } from '../../redux/builder.actions'
import { updatePresentationProperties } from '../../../../../../stores/presentation/presentation.actions'
import { errorHandler } from 'pobble-components/dist/error-handler'

const styles = {
  option: {
    '& > *': {
      padding: '10px 15px'
    }
  },
  gridCell: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gridGap: '16px'
  },
  ageRow: {
    display: 'flex',
    alignItems: 'flex-end'
  },
  showAge: {
    '& label': {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '10px',
      '& > *:first-child': {
        marginRight: '8px'
      }
    }
  }
}

const Settings = injectSheet(styles)(({ classes }) => {
  const dispatch = useDispatch()

  const presentation = useSelector(getPresentation)
  const yearGroupsSet = presentation.year_groups
  const accessibilityColor = presentation.accessibility_color
  const showAge = presentation.show_age
  const fontSize = presentation.font_size
  const updatingPropertiesRequest = useSelector(getUpdatePropertiesRequest)
  const pending = updatingPropertiesRequest.pending
  const allYearGroups = presentation.all_year_groups
  const fontType = presentation.font_type

  const yearGroupsSetIds = useMemo(
    () => (!yearGroupsSet ? null : yearGroupsSet.map(item => item.id)),
    [yearGroupsSet]
  )

  const [showAgeSelector, setShowAgeSelector] = useState(
    !allYearGroups && yearGroupsSetIds.length
  )

  return (
    <Fragment>
      <ModalHeader>Presentation settings</ModalHeader>
      <Formik
        onSubmit={values => {
          dispatch(
            updatePresentationProperties(presentation.id, {
              accessibility_color: values.accessibilityColor,
              font_size: values.fontSize,
              year_groups: values.yearGroups,
              show_age: values.showAge,
              font_type: values.fontType
            })
          ).catch(errorHandler)
        }}
        initialValues={{
          accessibilityColor,
          fontSize,
          yearGroups: yearGroupsSetIds,
          showAge,
          fontType
        }}>
        {({
          handleSubmit,
          isValid,
          values,
          submitForm,
          setFieldValue,
          handleChange
        }) => (
          <form onSubmit={handleSubmit}>
            <ModalContent legacy>
              <div className={classes.wrapper}>
                {!showAgeSelector && (
                  <FormRow>
                    <Button
                      action={() => setShowAgeSelector(true)}
                      tall
                      secondary>
                      Set age group
                    </Button>
                  </FormRow>
                )}
                {showAgeSelector && (
                  <FormRow className={classes.ageRow}>
                    <div>
                      <Label>Age range</Label>
                      <YearGroupSelection
                        selected={values.yearGroups}
                        onChange={({ selectedIds }) => {
                          setFieldValue('yearGroups', selectedIds)
                          if (!selectedIds.length) {
                            setShowAgeSelector(false)
                          }
                        }}
                      />
                    </div>
                    <div className={classes.showAge}>
                      <label>
                        <FormCheckbox
                          checked={values.showAge}
                          name="showAge"
                          onChange={handleChange}
                        />
                        Show on the title slide
                      </label>
                    </div>
                  </FormRow>
                )}
                <FormRow className={classes.gridCell}>
                  <div>
                    <Label>Font size</Label>
                    <FormSelect
                      optionClassName={classes.option}
                      value={values.fontSize}
                      onChange={selected =>
                        setFieldValue('fontSize', selected.value)
                      }
                      options={[
                        {
                          value: 'small',
                          label: 'Small'
                        },
                        {
                          value: 'medium',
                          label: 'Medium'
                        },
                        {
                          value: 'large',
                          label: 'Large'
                        }
                      ]}
                    />
                  </div>
                  <div>
                    <AccessibilitySelect
                      onChange={selected =>
                        setFieldValue('accessibilityColor', selected.value)
                      }
                      value={values.accessibilityColor}
                    />
                  </div>
                  <div>
                    <Label>Font type</Label>
                    <FormSelect
                      onChange={selected =>
                        setFieldValue('fontType', selected.value)
                      }
                      optionClassName={classes.option}
                      value={values.fontType}
                      options={[
                        {
                          value: 'default',
                          label: 'Default'
                        },
                        {
                          value: 'cursive_a',
                          label: 'Cursive C4'
                        },
                        {
                          value: 'cursive_b',
                          label: 'Cursive C6'
                        }
                      ]}
                    />
                  </div>
                </FormRow>
              </div>
            </ModalContent>
            <ModalFooter alignRight childrenGap>
              <Button tall simple action={() => dispatch(closeSettingsModal())}>
                Cancel
              </Button>
              <Button
                className="metrics__presentation-settings-save"
                minw
                pending={pending}
                tall
                type="submit">
                Save
              </Button>
            </ModalFooter>
          </form>
        )}
      </Formik>
    </Fragment>
  )
})

const SettingsModal = () => {
  const isOpen = useSelector(isSettingsModalOpened)
  const dispatch = useDispatch()

  return (
    <Modal
      isOpen={isOpen}
      closeButton
      requestClose={() => dispatch(closeSettingsModal())}>
      <Settings />
    </Modal>
  )
}

export default SettingsModal
